import { useState } from "react";
import { toast } from "sonner";
import { Heart, MessageCircle, Trash2, Send } from "lucide-react";
import { api, errText, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const Comment = ({ c, onDelete, t }) => (
  <div className="flex items-start gap-2" data-testid={`update-comment-${c.id}`}>
    {c.picture ? <img src={photoUrl(c.picture)} alt="" className="h-6 w-6 rounded-full object-cover shrink-0" />
      : <span className="h-6 w-6 rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 flex items-center justify-center shrink-0">{c.name?.[0]}</span>}
    <div className="flex-1 min-w-0 bg-slate-50 rounded-xl px-2.5 py-1.5">
      <p className="text-[11px] font-semibold text-[#12234A]">{c.name}{c.is_breeder && <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{t("rx_breeder")}</span>}</p>
      <p className="text-xs text-slate-700 whitespace-pre-line break-words">{c.text}</p>
    </div>
    {c.can_delete && <button onClick={() => onDelete(c.id)} aria-label={t("rx_delete")} data-testid={`update-comment-delete-${c.id}`} className="text-slate-500 hover:text-red-500 mt-1"><Trash2 size={12} /></button>}
  </div>
);

export function UpdateReactions({ update }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [hearts, setHearts] = useState({ hearts: update.hearts || 0, hearted: !!update.hearted });
  const [comments, setComments] = useState(update.comments || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const toggleHeart = async () => {
    try {
      const { data } = hearts.hearted ? await api.delete(`/litter-updates/${update.id}/heart`) : await api.post(`/litter-updates/${update.id}/heart`);
      setHearts(data);
    } catch (e) { toast.error(errText(e)); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/litter-updates/${update.id}/comments`, { text });
      setComments((l) => [...l, data]); setText("");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  const del = async (cid) => {
    try { await api.delete(`/litter-updates/${update.id}/comments/${cid}`); setComments((l) => l.filter((c) => c.id !== cid)); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`update-reactions-${update.id}`}>
      <div className="flex items-center gap-4 text-xs">
        <button onClick={toggleHeart} disabled={!user} data-testid={`update-heart-${update.id}`}
          className={`inline-flex items-center gap-1 font-semibold transition-transform active:scale-90 ${hearts.hearted ? "text-rose-500" : "text-slate-500 hover:text-rose-500"}`}>
          <Heart size={15} className={hearts.hearted ? "fill-rose-500" : ""} /> <span data-testid={`update-heart-count-${update.id}`}>{hearts.hearts}</span>
        </button>
        <span className="inline-flex items-center gap-1 text-slate-500"><MessageCircle size={14} /> {comments.length}</span>
      </div>
      {comments.length > 0 && <div className="space-y-1.5 mt-2">{comments.map((c) => <Comment key={c.id} c={c} onDelete={del} t={t} />)}</div>}
      {user && (
        <form onSubmit={send} className="flex items-center gap-1.5 mt-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder={t("rx_comment_ph")} data-testid={`update-comment-input-${update.id}`}
            className="flex-1 h-8 rounded-full border border-slate-200 px-3 text-xs focus:outline-none focus:border-[#F5551E]" />
          <button type="submit" disabled={busy || !text.trim()} aria-label={t("rx_send")} data-testid={`update-comment-send-${update.id}`} className="h-8 w-8 rounded-full bg-[#F5551E] text-white flex items-center justify-center disabled:opacity-40"><Send size={13} /></button>
        </form>
      )}
    </div>
  );
}
